/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from "../../ComponentLogger";

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class NodeWidget extends cc.Component
{
    @property(cc.Node)
    protected horizontalNode: cc.Node = null;
    @property(cc.Float)
    protected horizontalOffset: number = 0;

    @property(cc.Node)
    protected verticalNode: cc.Node = null;
    @property(cc.Float)
    protected verticalOffset: number = 0;

    @property(cc.Node)
    protected watchNode: cc.Node = null;
    @property()
    protected eventName: string = 'size-changed';
    @property()
    protected alignToChild: boolean = false;

    /** @hidden */
    protected onLoad(): void
    {
        if (!this.watchNode)
        {
            CLog.error('No watch node configured', this, LogTypes.Initialization);
            return;
        }

        this.align();
        this.watchNode.on(this.eventName, () =>
        {
            this.align();
        });
    }

    private align(): void
    {
        this.alignHorizontal();
        this.alignVertical();
    }

    private alignHorizontal(): void
    {
        if (!this.horizontalNode) { return; }

        const leftPos: cc.Vec2 = this.getWorldPosition(this.watchNode);
        const rightPos: number = leftPos.x + this.watchNode.width;

        let newPos: cc.Vec2 = new cc.Vec2(((rightPos + leftPos.x) / 2) - (this.node.width / 2), 0);
        newPos.x += this.horizontalOffset;

        this.setWorldPosition(newPos, 'x');
    }

    private alignVertical(): void
    {
        if (!this.verticalNode) { return; }

        const topPos: cc.Vec2 = this.getWorldPosition(this.watchNode);
        const bottomPos: number = topPos.y - this.watchNode.height;

        let newPos: cc.Vec2 = new cc.Vec2(0, ((bottomPos + topPos.y) / 2) + (this.node.height / 2));
        newPos.y += this.verticalOffset;

        this.setWorldPosition(newPos, 'y');
    }

    private getWorldPosition(node: cc.Node): cc.Vec2
    {
        if (!node || !node.parent) { return null; }

        if (this.alignToChild && node.children) {
            let childIndex: number = node.children.length;
            childIndex = childIndex > 1 ? Math.floor(childIndex / 2) - 1 : 0;
            node = node.children[childIndex] || node;
        }

        const position: cc.Vec2 = node.parent.convertToWorldSpaceAR(node.position);
        const anchorPoints: cc.Size = this.getAnchorSize(node);

        position.x -= anchorPoints.width;
        position.y -= anchorPoints.height;

        return position;
    }

    private setWorldPosition(position: cc.Vec2, propertyId: string): this
    {
        const newPos: cc.Vec2 = this.node.parent.convertToNodeSpaceAR(position);
        const anchorPoints: cc.Size = this.getAnchorSize(this.node);

        newPos.x += anchorPoints.width;
        newPos.y += anchorPoints.height;

        this.node[propertyId] = newPos[propertyId];
        return this;
    }

    private getAnchorSize(node: cc.Node): cc.Size
    {
        let width: number = node.anchorX * node.width;
        let height: number = node.anchorY * node.height;
        return new cc.Size(width, height);
    }
}
